import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Layout } from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  QrCode,
  Calendar,
  MapPin,
  Users,
  UserCheck,
  ChevronRight,
  Loader2,
  Clock
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function EventCheckinListPage() {
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [attendanceData, setAttendanceData] = useState({});

  const getToken = () => localStorage.getItem('shrt_token');

  useEffect(() => {
    if (!isAdmin) {
      navigate('/');
      return;
    }
    fetchEvents();
  }, [isAdmin]);

  const fetchEvents = async () => {
    const token = getToken();
    try {
      const response = await axios.get(`${API_URL}/api/events`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Extract events array from response
      const eventsData = response.data.events || response.data || [];
      
      // Sort by date descending (newest first)
      const sortedEvents = eventsData.sort((a, b) => 
        new Date(b.date) - new Date(a.date)
      );
      
      setEvents(sortedEvents);
      
      // Fetch attendance for each event
      for (const event of sortedEvents) {
        try {
          const attendanceRes = await axios.get(
            `${API_URL}/api/events/${event.id}/attendance`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          setAttendanceData(prev => ({
            ...prev,
            [event.id]: attendanceRes.data.summary
          }));
        } catch (err) {
          // Event might not have registrations
        }
      }
    } catch (error) {
      toast.error('Fehler beim Laden der Events');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('de-CH', {
      weekday: 'short',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const isPastEvent = (dateStr) => {
    if (!dateStr) return false;
    const eventDate = new Date(dateStr);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return eventDate < today;
  };

  const isToday = (dateStr) => {
    if (!dateStr) return false;
    const eventDate = new Date(dateStr);
    const today = new Date();
    return eventDate.toDateString() === today.toDateString();
  };

  // Separate events
  const todayEvents = events.filter(e => isToday(e.date));
  const upcomingEvents = events.filter(e => !isPastEvent(e.date) && !isToday(e.date));
  const pastEvents = events.filter(e => isPastEvent(e.date));

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[50vh]">
          <Loader2 className="animate-spin" size={32} />
        </div>
      </Layout>
    );
  }

  const EventCard = ({ event }) => {
    const attendance = attendanceData[event.id];
    const past = isPastEvent(event.date);
    const today = isToday(event.date);
    
    return (
      <Link 
        to={`/events/${event.id}/checkin`}
        className={`block bg-card border border-border p-4 hover:border-primary transition-colors ${
          today ? 'ring-2 ring-primary' : ''
        }`}
      >
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              {today && (
                <span className="px-2 py-0.5 bg-primary text-primary-foreground text-xs font-bold uppercase">
                  Heute
                </span>
              )}
              <h3 className="font-['Oswald'] text-lg font-bold truncate">
                {event.title}
              </h3>
            </div>
            <div className="flex flex-wrap gap-3 text-sm text-muted-foreground">
              <span className="flex items-center gap-1">
                <Calendar size={14} />
                {formatDate(event.date)}
              </span>
              {event.time && (
                <span className="flex items-center gap-1">
                  <Clock size={14} />
                  {event.time} Uhr
                </span>
              )}
              {event.location && (
                <span className="flex items-center gap-1">
                  <MapPin size={14} />
                  {event.location}
                </span>
              )}
            </div>
          </div>
          
          <div className="flex items-center gap-4">
            {attendance && (
              <div className="text-right">
                <div className="flex items-center gap-2">
                  <UserCheck size={18} className="text-green-500" />
                  <span className="text-lg font-bold">
                    {attendance.checked_in_count}/{attendance.total_registrations}
                  </span>
                </div>
                <p className="text-xs text-muted-foreground">eingecheckt</p>
              </div>
            )}
            <ChevronRight size={20} className="text-muted-foreground" />
          </div>
        </div>
      </Link>
    );
  };

  return (
    <Layout>
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="font-['Oswald'] text-3xl sm:text-4xl font-bold uppercase tracking-tight mb-2 flex items-center gap-3">
            <QrCode className="text-primary" size={36} />
            Event Check-in
          </h1>
          <p className="text-muted-foreground">
            Wählen Sie ein Event für den Einlass-Check-in
          </p>
        </div>

        {/* Today's Events */}
        {todayEvents.length > 0 && (
          <div className="mb-8">
            <h2 className="font-['Oswald'] text-sm font-bold uppercase tracking-wider mb-3 text-primary flex items-center gap-2">
              <Calendar size={16} />
              Heute ({todayEvents.length})
            </h2>
            <div className="space-y-3">
              {todayEvents.map(event => (
                <EventCard key={event.id} event={event} />
              ))}
            </div>
          </div>
        )}

        {/* Upcoming Events */}
        {upcomingEvents.length > 0 && (
          <div className="mb-8">
            <h2 className="font-['Oswald'] text-sm font-bold uppercase tracking-wider mb-3 text-muted-foreground flex items-center gap-2">
              <Calendar size={16} />
              Kommende Events ({upcomingEvents.length})
            </h2>
            <div className="space-y-3">
              {upcomingEvents.map(event => (
                <EventCard key={event.id} event={event} />
              ))}
            </div>
          </div>
        )}

        {/* Past Events */}
        {pastEvents.length > 0 && (
          <div className="mb-8">
            <h2 className="font-['Oswald'] text-sm font-bold uppercase tracking-wider mb-3 text-muted-foreground flex items-center gap-2">
              <Clock size={16} />
              Vergangene Events ({pastEvents.length})
            </h2>
            <div className="space-y-3 opacity-70">
              {pastEvents.slice(0, 5).map(event => (
                <EventCard key={event.id} event={event} />
              ))}
              {pastEvents.length > 5 && (
                <p className="text-sm text-muted-foreground text-center py-2">
                  + {pastEvents.length - 5} weitere vergangene Events
                </p>
              )}
            </div>
          </div>
        )}

        {events.length === 0 && (
          <div className="text-center py-12 bg-card border border-border">
            <QrCode size={48} className="mx-auto mb-4 text-muted-foreground" />
            <p className="text-muted-foreground">Keine Events vorhanden</p>
            <Link to="/events/new" className="text-primary hover:underline mt-2 inline-block">
              Event erstellen
            </Link>
          </div>
        )}
      </div>
    </Layout>
  );
}
